import type { Result } from '../core/result';
import { ok, err } from '../core/result';
import type { Rng } from '../core/rng';
import type { LinearRGB } from '../core/color';
import type {
  RawPalette,
  PaletteModel,
  PaletteClass,
  Swatch,
  WeightedSwatch,
  DataError,
  PaletteClassName,
} from './types';
import { REQUIRED_PALETTE_CLASSES } from './types';

function toSwatch(raw: { hex: string; linear: readonly [number, number, number] }): Swatch {
  return {
    hex: raw.hex,
    linear: raw.linear,
  };
}

function toWeightedSwatch(raw: {
  hex: string;
  linear: readonly [number, number, number];
  weight: number;
}): WeightedSwatch {
  return {
    hex: raw.hex,
    linear: raw.linear,
    weight: raw.weight,
  };
}

interface RawClassStats {
  median: { hex: string; linear: readonly [number, number, number] };
  p10: { hex: string; linear: readonly [number, number, number] };
  p90: { hex: string; linear: readonly [number, number, number] };
  centres: ReadonlyArray<{
    hex: string;
    linear: readonly [number, number, number];
    weight: number;
  }>;
}

function toPaletteClass(raw: RawClassStats): PaletteClass {
  return {
    median: toSwatch(raw.median),
    p10: toSwatch(raw.p10),
    p90: toSwatch(raw.p90),
    centres: raw.centres.map(toWeightedSwatch),
  };
}

export function toPaletteModel(raw: RawPalette): Result<PaletteModel, DataError> {
  const missingClasses: string[] = [];
  const requiredClasses: readonly PaletteClassName[] = REQUIRED_PALETTE_CLASSES;

  const classes = raw.classes;
  if (!classes) {
    return err({
      file: 'palette',
      kind: 'schema',
      message: 'Missing classes in palette',
      details: ['Expected classes object with palette class data'],
    });
  }

  for (const cls of requiredClasses) {
    if (!(cls in classes)) {
      missingClasses.push(cls);
    }
  }

  if (missingClasses.length > 0) {
    return err({
      file: 'palette',
      kind: 'schema',
      message: 'Missing required palette classes',
      details: missingClasses,
    });
  }

  const model = {} as Record<PaletteClassName, PaletteClass>;
  for (const cls of requiredClasses) {
    const rawClass = classes[cls] as RawClassStats | undefined;
    if (rawClass) {
      model[cls] = toPaletteClass(rawClass);
    }
  }

  return ok(model as PaletteModel);
}

export function sampleSwatch(cls: PaletteClass, rng: Rng): LinearRGB {
  if (cls.centres.length === 0) {
    return cls.median.linear;
  }

  const items = cls.centres;
  const weights = items.map((c) => c.weight);

  const selected = rng.weighted(items, weights);
  return selected.linear;
}
